import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { ClipboardList, User, Briefcase, FileText, Calendar, CheckCircle, Shield, Search, Filter, LogIn, Trash2, Upload, Download, Edit2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const entityIcons = {
  position: Briefcase, resume: FileText, resumes: FileText,
  interview: Calendar, interviews: Calendar,
  closure: CheckCircle, closures: CheckCircle,
  joining: Shield, joinings: Shield,
  auth: LogIn, policy: FileText, talent_vault: Upload,
  leave: Calendar,
};

const actionColors = {
  created: 'bg-emerald-100 text-emerald-800',
  bulk_status_update: 'bg-cyan-100 text-cyan-800',
  status_change: 'bg-amber-100 text-amber-800',
  updated: 'bg-blue-100 text-blue-800',
  deleted: 'bg-red-100 text-red-800',
  login: 'bg-indigo-100 text-indigo-800',
  uploaded_jd: 'bg-purple-100 text-purple-800',
  edited: 'bg-blue-100 text-blue-800',
  exported: 'bg-teal-100 text-teal-800',
  imported: 'bg-orange-100 text-orange-800',
};

export default function ActivityLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterUser, setFilterUser] = useState('');
  const [filterAction, setFilterAction] = useState('all');
  const [filterEntity, setFilterEntity] = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [limit, setLimit] = useState(200);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ limit: limit.toString() });
      if (filterAction !== 'all') params.set('action', filterAction);
      if (filterEntity !== 'all') params.set('entity_type', filterEntity);
      if (filterDateFrom) params.set('date_from', filterDateFrom);
      if (filterDateTo) params.set('date_to', filterDateTo);
      const res = await axios.get(`${API}/activity-log/detailed?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(res.data);
    } catch {
      toast.error('Failed to fetch activity log');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLogs(); }, [filterAction, filterEntity, filterDateFrom, filterDateTo, limit]);

  const filteredLogs = useMemo(() => {
    if (!filterUser) return logs;
    const q = filterUser.toLowerCase();
    return logs.filter(l => (l.user_name || '').toLowerCase().includes(q));
  }, [logs, filterUser]);

  const actions = useMemo(() => [...new Set(logs.map(l => l.action).filter(Boolean))].sort(), [logs]);
  const entities = useMemo(() => [...new Set(logs.map(l => l.entity_type).filter(Boolean))].sort(), [logs]);

  const timeAgo = (timestamp) => {
    const diff = Date.now() - new Date(timestamp).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  const formatDate = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) + ' ' + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  const clearFilters = () => {
    setFilterUser(''); setFilterAction('all'); setFilterEntity('all');
    setFilterDateFrom(''); setFilterDateTo('');
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-blue-900 mb-2" data-testid="activity-log-title" style={{ fontFamily: 'Manrope, sans-serif' }}>Activity Log</h1>
          <p className="text-slate-600">Comprehensive audit trail of all actions across the platform</p>
        </div>
        <div className="flex items-center gap-2">
          <select data-testid="log-limit" value={limit} onChange={e => setLimit(parseInt(e.target.value))} className="h-9 rounded border border-blue-200 bg-white px-3 text-sm">
            <option value={100}>Last 100</option>
            <option value={200}>Last 200</option>
            <option value={500}>Last 500</option>
            <option value={1000}>Last 1000</option>
          </select>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-blue-100 rounded shadow-sm p-4 mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[180px]">
            <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">User</Label>
            <div className="relative mt-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input data-testid="log-filter-user" value={filterUser} onChange={e => setFilterUser(e.target.value)} placeholder="Filter by name..." className="pl-9 h-9 rounded border-blue-200 text-sm" />
            </div>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Action</Label>
            <select data-testid="log-filter-action" value={filterAction} onChange={e => setFilterAction(e.target.value)} className="mt-1 flex h-9 rounded border border-blue-200 bg-white px-3 py-1 text-sm">
              <option value="all">All Actions</option>
              {actions.map(a => <option key={a} value={a}>{a.replace('_', ' ')}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Entity</Label>
            <select data-testid="log-filter-entity" value={filterEntity} onChange={e => setFilterEntity(e.target.value)} className="mt-1 flex h-9 rounded border border-blue-200 bg-white px-3 py-1 text-sm">
              <option value="all">All Entities</option>
              {entities.map(e => <option key={e} value={e}>{e}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">From</Label>
            <Input data-testid="log-filter-from" type="date" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)} className="mt-1 h-9 rounded border-blue-200 text-sm w-36" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">To</Label>
            <Input data-testid="log-filter-to" type="date" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)} className="mt-1 h-9 rounded border-blue-200 text-sm w-36" />
          </div>
          <Button data-testid="log-clear-filters" variant="ghost" onClick={clearFilters} className="text-slate-500 text-xs h-9">Clear</Button>
        </div>
        <div className="mt-2 text-xs text-slate-500" data-testid="log-count">{filteredLogs.length} log entries</div>
      </div>

      {/* Logs List */}
      {loading ? (
        <div className="flex items-center justify-center h-48"><p className="text-slate-500">Loading activity log...</p></div>
      ) : (
        <div className="space-y-2">
          {filteredLogs.length === 0 ? (
            <div className="bg-white border border-blue-100 rounded p-12 text-center">
              <ClipboardList size={48} className="mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500" data-testid="no-logs-message">No activity recorded.</p>
            </div>
          ) : (
            filteredLogs.map((log) => {
              const Icon = entityIcons[log.entity_type] || ClipboardList;
              const actionCls = actionColors[log.action] || 'bg-slate-100 text-slate-800';
              return (
                <div key={log.id} data-testid={`log-entry-${log.id}`} className="bg-white border border-blue-100 rounded p-4 flex items-start gap-4 hover:shadow-sm transition-shadow">
                  <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <Icon size={18} className="text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-blue-900">{log.user_name}</span>
                      <span className={`px-2 py-0.5 text-xs font-bold uppercase rounded ${actionCls}`}>{log.action?.replace('_', ' ')}</span>
                      <span className="text-xs text-slate-500 font-medium uppercase bg-slate-50 px-1.5 py-0.5 rounded">{log.entity_type}</span>
                    </div>
                    {log.details && <p className="text-sm text-slate-600 mt-1">{log.details}</p>}
                    <p className="text-xs text-slate-400 mt-1">{formatDate(log.timestamp)}</p>
                  </div>
                  <span className="text-xs text-slate-400 whitespace-nowrap flex-shrink-0">{timeAgo(log.timestamp)}</span>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
